<script>
import { h, resolveComponent } from 'vue';

import { PLACEMENT_LEFT, PLACEMENT_RIGHT } from '../../config/constants';
import { getSlotContent } from '../../utils/component';
import {
  DROPDOWN_ACTIVE_ITEM_DATA_ATTRIBUTE,
  DROPDOWN_ITEM_COLORS,
  DROPDOWN_ITEM_ICON_POSITIONS,
} from './config';

export default {
  name: 'DpDropdownItem',

  compatConfig: { MODE: 3 },

  inject: {
    onSelect: {
      default: () => {
      },
    },
  },

  inheritAttrs: false,

  props: {
    text: {
      type: String,
      required: true,
      default: null,
    },

    /**
     * Long text inside Dropdown items will be truncated
     * */
    textTruncate: {
      type: Boolean,
      required: false,
      default: true,
    },

    additionalText: {
      type: String,
      required: false,
      default: null,
    },

    color: {
      type: String,
      required: false,
      default: null,
      validator: (color) => DROPDOWN_ITEM_COLORS.includes(color),
    },

    href: {
      type: String,
      required: false,
      default: null,
    },

    to: {
      type: [String, Object],
      required: false,
      default: null,
    },

    active: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * @default left
     * */
    iconPosition: {
      type: String,
      required: false,
      default: PLACEMENT_LEFT,
      validator: (position) => DROPDOWN_ITEM_ICON_POSITIONS.includes(position),
    },

    iconOffset: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  methods: {
    prepareContent() {
      const defaultSlot = getSlotContent(this.$slots.default);

      if (defaultSlot) {
        return defaultSlot;
      }

      const content = [h(
        'span',
        {
          class: {
            'dropdown-item-text': true,
            'text-truncate': this.textTruncate,
          },
          attrs: {
            title: this.textTruncate ? this.text : null,
          },
        },
        this.text,
      )];
      const { $slots, additionalText, iconPosition } = this;

      if (additionalText) {
        content.push(h(
          'span',
          {
            class: 'text-placeholder small to-right pr-1-5',
          },
          [additionalText],
        ));
      }

      if ($slots.icon) {
        if (iconPosition === PLACEMENT_LEFT) {
          content.unshift(
            h(
              'span',
              {
                class: `to-${iconPosition} py-1 px-1-5`,
              },
              [
                $slots.icon(),
              ],
            ),
          );
        } else {
          content.push(
            h(
              'span',
              {
                class: `to-${iconPosition} py-1 px-1-5`,
              },
              [
                $slots.icon(),
              ],
            ),
          );
        }
      }

      return content;
    },
  },

  /**
   * @slot icon
   */
  render() {
    const { $attrs, active, color, disabled, iconOffset, to } = this;
    const content = this.prepareContent();
    const classes = [{
      active,
      disabled,
      'icon-offset': iconOffset,
      'dropdown-item': true,
      'dropdown-item-icon-on-left': this.$slots.icon && this.iconPosition === PLACEMENT_LEFT,
      'dropdown-item-icon-on-right': this.$slots.icon && this.iconPosition === PLACEMENT_RIGHT,
    }];

    if (color) {
      classes.push(`text-${color}`);
    }

    if (to) {
      return h(
        'li',
        [
          h(
            resolveComponent('router-link'),
            {
              class: classes,
              to,
            },
            {
              default: () => content,
            },
          ),
        ],
      );
    }

    const { onClick, ...attrs } = $attrs;

    const clickHandler = (event) => {
      if (typeof onClick === 'function') {
        onClick(event);
      }

      if (!this.href || disabled) {
        event.preventDefault();
      }

      if (!disabled) {
        this.onSelect(event, {
          text: this.text,
          to: this.to,
          active: this.active,
        });
      }
    };

    return h(
      'li',
      [
        h(
          'a',
          {
            ...attrs,
            href: this.href || '#',
            [DROPDOWN_ACTIVE_ITEM_DATA_ATTRIBUTE]: this.active,
            onClick: clickHandler,
            class: classes,
          },
          {
            default: () => content,
          },
        ),
      ],
    );
  },
};
</script>
